/**
 * MenuPluginManager.ts - Menu Plugin Management System
 * 
 * Manages plugin-registered menu items with full orchestrator integration
 * Allows plugins to dynamically add/remove menu items
 * 
 * FEATURES:
 * - Plugin item registration/unregistration
 * - Action logging (Actr)
 * - Event system for item changes
 * - Menu-specific item filtering
 * - Lifecycle management
 * 
 * ARCHITECTURE:
 * - Integrates with @opcentral/pluggr for plugin lifecycle
 * - Uses @opcentral/actr for action logging
 * - Observable pattern for reactive updates
 * 
 * CHANGE HISTORY:
 * - 251115_1840 [current] danielbeer: Created with orchestrator integration
 */

import type { MenuItem } from './types';

export interface MenuItemRegistration {
  menu: 'side' | 'top' | 'context';
  item: MenuItem;
}

export interface MenuPluginManagerOptions {
  actr?: any; // Actr instance for logging
}

export class MenuPluginManager {
  private registeredItems: Map<string, MenuItem> = new Map();
  private listeners: Set<() => void> = new Set();
  private actr: any;
  
  constructor(options: MenuPluginManagerOptions = {}) {
    this.actr = options.actr;
  }
  
  /**
   * Register a menu item from a plugin
   */
  registerMenuItem(config: MenuItemRegistration): void {
    const { menu, item } = config;
    const key = `${menu}:${item.id}`;
    
    // Check if already registered
    if (this.registeredItems.has(key)) {
      console.warn(`Menu item ${item.id} already registered in ${menu} menu`);
      return;
    }
    
    // Mark as plugin-sourced
    const pluginItem: MenuItem = {
      ...item,
      metadata: {
        ...item.metadata,
        source: 'plugin',
        registeredAt: Date.now()
      }
    };
    
    this.registeredItems.set(key, pluginItem);
    
    // Log action
    this.actr?.execute('menu:plugin-item-registered', {
      menu,
      itemId: item.id,
      label: item.label,
      type: item.type
    });
    
    // Notify listeners
    this.notifyListeners();
  }
  
  /**
   * Unregister a menu item
   */
  unregisterMenuItem(itemId: string, menu?: 'side' | 'top' | 'context'): void {
    let removed = false;
    
    if (menu) {
      // Remove from specific menu
      const key = `${menu}:${itemId}`;
      if (this.registeredItems.has(key)) {
        this.registeredItems.delete(key);
        removed = true;
        
        this.actr?.execute('menu:plugin-item-unregistered', {
          menu,
          itemId
        });
      }
    } else {
      // Remove from all menus
      for (const [key, item] of this.registeredItems.entries()) {
        if (item.id === itemId) {
          const [menuType] = key.split(':');
          this.registeredItems.delete(key);
          removed = true;
          
          this.actr?.execute('menu:plugin-item-unregistered', {
            menu: menuType,
            itemId
          });
        }
      }
    }
    
    if (removed) {
      this.notifyListeners();
    }
  }
  
  /**
   * Update a menu item
   */
  updateMenuItem(itemId: string, updates: Partial<MenuItem>, menu?: 'side' | 'top' | 'context'): void {
    let updated = false;
    
    if (menu) {
      // Update in specific menu
      const key = `${menu}:${itemId}`;
      const item = this.registeredItems.get(key);
      
      if (item) {
        const updatedItem = { ...item, ...updates };
        this.registeredItems.set(key, updatedItem);
        updated = true;
        
        this.actr?.execute('menu:plugin-item-updated', {
          menu,
          itemId,
          updates: Object.keys(updates)
        });
      }
    } else {
      // Update in all menus
      for (const [key, item] of this.registeredItems.entries()) {
        if (item.id === itemId) {
          const [menuType] = key.split(':');
          const updatedItem = { ...item, ...updates };
          this.registeredItems.set(key, updatedItem);
          updated = true;
          
          this.actr?.execute('menu:plugin-item-updated', {
            menu: menuType,
            itemId,
            updates: Object.keys(updates)
          });
        }
      }
    }
    
    if (updated) {
      this.notifyListeners();
    }
  }
  
  /**
   * Get all menu items for a specific menu
   */
  getMenuItems(menuId: 'side' | 'top' | 'context'): MenuItem[] {
    const items: MenuItem[] = [];
    const prefix = `${menuId}:`;
    
    for (const [key, item] of this.registeredItems.entries()) {
      if (key.startsWith(prefix)) {
        items.push(item);
      }
    }
    
    return items;
  }
  
  /**
   * Get all registered items across all menus
   */
  getAllItems(): Map<string, MenuItem> {
    return new Map(this.registeredItems);
  }
  
  /**
   * Check if an item is registered
   */
  hasItem(itemId: string, menu?: 'side' | 'top' | 'context'): boolean {
    if (menu) {
      const key = `${menu}:${itemId}`;
      return this.registeredItems.has(key);
    }
    
    for (const item of this.registeredItems.values()) {
      if (item.id === itemId) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Clear all registered items
   */
  clear(menu?: 'side' | 'top' | 'context'): void {
    if (menu) {
      // Clear specific menu
      const prefix = `${menu}:`;
      const keysToDelete: string[] = [];
      
      for (const key of this.registeredItems.keys()) {
        if (key.startsWith(prefix)) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => this.registeredItems.delete(key));
      
      this.actr?.execute('menu:plugin-items-cleared', {
        menu,
        count: keysToDelete.length
      });
    } else {
      // Clear all menus
      const count = this.registeredItems.size;
      this.registeredItems.clear();
      
      this.actr?.execute('menu:plugin-items-cleared', {
        menu: 'all',
        count
      });
    }
    
    this.notifyListeners();
  }
  
  /**
   * Subscribe to item changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }
  
  /**
   * Get statistics
   */
  getStats(): {
    totalItems: number;
    sideMenuItems: number;
    topMenuItems: number;
    contextMenuItems: number;
  } {
    let sideMenuItems = 0;
    let topMenuItems = 0;
    let contextMenuItems = 0;
    
    for (const key of this.registeredItems.keys()) {
      if (key.startsWith('side:')) sideMenuItems++;
      else if (key.startsWith('top:')) topMenuItems++;
      else if (key.startsWith('context:')) contextMenuItems++;
    }
    
    return {
      totalItems: this.registeredItems.size,
      sideMenuItems,
      topMenuItems,
      contextMenuItems
    };
  }
  
  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in menu plugin listener:', error);
      }
    });
  }
}

// Singleton instance
let globalInstance: MenuPluginManager | null = null;

/**
 * Get or create the global MenuPluginManager instance
 */
export function getMenuPluginManager(options?: MenuPluginManagerOptions): MenuPluginManager {
  if (!globalInstance) {
    globalInstance = new MenuPluginManager(options);
  }
  return globalInstance;
}

/**
 * Reset the global instance (useful for testing)
 */
export function resetMenuPluginManager(): void {
  globalInstance = null;
}
